import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash2, Plus } from 'lucide-react';

function ProjectManagement() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTaxInvoice, setFilterTaxInvoice] = useState('');
  const [filterVat, setFilterVat] = useState('');
  const [filterComprehensive, setFilterComprehensive] = useState('');
  const [filterContractType, setFilterContractType] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [clientDetail, setClientDetail] = useState(null);

  function openDetailModal(project) {
    setDetailProject(project);
    setShowDetailModal(true);
  }
  function closeDetailModal() {
    setShowDetailModal(false);
    setDetailProject(null);
  }

  function openClientDetailModal(project) {
    setClientDetail(project.companies);
    setShowClientInfoModal(true);
  }
  function closeClientDetailModal() {
    setShowClientInfoModal(false);
    setClientDetail(null);
  }

  // Form data for create/edit
  const [formData, setFormData] = useState({
    project_name: '',
    company_id: '',
    status: '진행중',
    payment_status: '대기',
    amount: '',
    vat_amount: '',
    is_business: false,
    tax_invoice_status: '발급 필요',
    vat_reporting_status: '신고 필요',
    comprehensive_tax_status: '신고 필요',
    contract_type: '계좌이체',
    contract_date: new Date().toISOString().split('T')[0],
    deadline: '',
    advance_payment_date: '',
    interim_payment_date: '',
    final_payment_date: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load projects and companies in parallel
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const [projectsResponse, companiesResponse] = await Promise.all([
        fetch(`${baseUrl}/api/projects`),
        fetch(`${baseUrl}/api/companies`)
      ]);

      const projectsData = await projectsResponse.json();
      const companiesData = await companiesResponse.json();

      // Sort companies by most recent project contract date
      const companiesWithProjects = companiesData.map(company => {
        const companyProjects = projectsData.filter(p => p.company_id === company.id);
        const mostRecentProject = companyProjects.sort((a, b) => {
          if (!a.contract_date && !b.contract_date) return 0;
          if (!a.contract_date) return 1;
          if (!b.contract_date) return -1;
          return new Date(b.contract_date) - new Date(a.contract_date);
        })[0];
        
        return {
          ...company,
          mostRecentContractDate: mostRecentProject?.contract_date || company.created_at
        };
      });

      const sortedCompanies = companiesWithProjects.sort((a, b) => {
        return new Date(b.mostRecentContractDate) - new Date(a.mostRecentContractDate);
      });

      setCompanies(sortedCompanies);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      project_name: '',
      company_id: '',
      status: '진행중',
      payment_status: '대기',
      amount: '',
      vat_amount: '',
      is_business: false,
      tax_invoice_status: '필요',
      vat_reporting_status: '필요',
      comprehensive_tax_status: '필요',
      contract_type: '계좌이체',
      contract_date: new Date().toISOString().split('T')[0],
      deadline: '',
      advance_payment_date: '',
      interim_payment_date: '',
      final_payment_date: ''
    });
    setSelectedClient(null);
    setShowCreateModal(true);
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      project_name: project.project_name,
      company_id: project.company_id,
      status: project.status,
      payment_status: project.payment_status,
      amount: formatNumberForDisplay(project.amount),
      vat_amount: formatNumberForDisplay(project.vat_amount),

      tax_invoice_status: project.tax_invoice_status,
      vat_reporting_status: project.vat_reporting_status,
      comprehensive_tax_status: project.comprehensive_tax_status,
      contract_type: project.contract_type,
      contract_date: project.contract_date || '',
      deadline: project.deadline || '',
      advance_payment_date: project.advance_payment_date || '',
      interim_payment_date: project.interim_payment_date || '',
      final_payment_date: project.final_payment_date || ''
    });
    setSelectedClient(project.companies);
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowClientModal(false);
    setSelectedProject(null);
    setSelectedClient(null);
  };

  const openClientModal = () => {
    setShowClientModal(true);
  };

  const closeClientModal = () => {
    setShowClientModal(false);
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, company_id: client.id }));
    setShowClientModal(false);
  };

  const handleInputChange = (field, value) => {
    let newValue = value;
    
    // Handle number formatting for amount and vat_amount fields
    if (field === 'amount' || field === 'vat_amount') {
      // Remove all non-digit characters except commas
      const numericValue = value.replace(/[^\d,]/g, '');
      
      // Remove existing commas and add new ones every 3 digits
      const cleanValue = numericValue.replace(/,/g, '');
      if (cleanValue) {
        newValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      } else {
        newValue = '';
      }
    }
    
    setFormData(prev => {
      const updatedData = { ...prev, [field]: newValue };
      
      // Auto-calculate VAT when amount changes and tax_invoice_status is '필요'
      if (field === 'amount' && prev.tax_invoice_status === '필요') {
        const numericAmount = newValue.replace(/,/g, '');
        if (numericAmount && !isNaN(numericAmount)) {
          const calculatedVat = Math.round(parseFloat(numericAmount) * 0.1);
          updatedData.vat_amount = calculatedVat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else {
          updatedData.vat_amount = '';
        }
      }
      
      // Auto-calculate VAT when tax_invoice_status changes to '필요'
      if (field === 'tax_invoice_status' && newValue === '필요') {
        const numericAmount = prev.amount.replace(/,/g, '');
        if (numericAmount && !isNaN(numericAmount)) {
          const calculatedVat = Math.round(parseFloat(numericAmount) * 0.1);
          updatedData.vat_amount = calculatedVat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
      }
      
      // Handle business checkbox logic
      if (field === 'is_business') {
        if (newValue) {
          // If business is checked, set tax-related fields to required
          updatedData.tax_invoice_status = '필요';
          updatedData.vat_reporting_status = '필요';
          updatedData.comprehensive_tax_status = '필요';
          updatedData.contract_type = '사업자';
          
          // Auto-calculate VAT if amount exists
          const numericAmount = prev.amount.replace(/,/g, '');
          if (numericAmount && !isNaN(numericAmount)) {
            const calculatedVat = Math.round(parseFloat(numericAmount) * 0.1);
            updatedData.vat_amount = calculatedVat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        } else {
          // If business is unchecked, set tax-related fields to not required
          updatedData.tax_invoice_status = '불필요';
          updatedData.vat_reporting_status = '불필요';
          updatedData.comprehensive_tax_status = '불필요';
          updatedData.contract_type = '계좌이체';
          updatedData.vat_amount = '';
        }
      }
      
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const url = showEditModal 
        ? `${baseUrl}/api/projects/${selectedProject.id}`
        : `${baseUrl}/api/projects`;
      
      const method = showEditModal ? 'PUT' : 'POST';
      
      // Parse numbers before sending to backend
      const submitData = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount.replace(/,/g, '')) : null,
        vat_amount: formData.vat_amount ? parseFloat(formData.vat_amount.replace(/,/g, '')) : null
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        closeModal();
        loadData();
      } else {
        const errorData = await response.json();
        alert(`오류: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error saving project:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('정말로 이 프로젝트를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${baseUrl}/api/projects/${projectId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('프로젝트가 삭제되었습니다.');
        loadData();
      } else {
        alert('삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const formatNumberForDisplay = (value) => {
    if (!value) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Sort projects by status priority and contract date
  const sortProjects = (projects) => {
    const statusPriority = {
      '진행중': 1,
      '납품': 2,
      '완료': 3,
      '취소': 4
    };

    return projects.sort((a, b) => {
      const statusA = statusPriority[a.status] || 999;
      const statusB = statusPriority[b.status] || 999;
      
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      
      // If same status, sort by contract date (descending)
      if (!a.contract_date && !b.contract_date) return 0;
      if (!a.contract_date) return 1;
      if (!b.contract_date) return -1;
      return new Date(b.contract_date) - new Date(a.contract_date);
    });
  };

  // Update filteredProjects to apply all filters and search
  const filteredProjects = projects.filter(project => {
    const matchesSearch =
      !searchTerm ||
      (project.project_name && project.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.companies?.client_name && project.companies.client_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !filterStatus || project.status === filterStatus;
    const matchesTaxInvoice = !filterTaxInvoice || getTaxInvoiceDisplay(project.tax_invoice_status) === filterTaxInvoice;
    const matchesVat = !filterVat || getVatDisplay(project.vat_reporting_status) === filterVat;
    const matchesComprehensive = !filterComprehensive || getVatDisplay(project.comprehensive_tax_status) === filterComprehensive;
    const matchesContractType = !filterContractType || project.contract_type === filterContractType;
    return matchesSearch && matchesStatus && matchesTaxInvoice && matchesVat && matchesComprehensive && matchesContractType;
  });

  const sortedProjects = sortProjects(filteredProjects);

  const filteredClients = companies.filter(client => {
    const searchTerm = clientSearchTerm.toLowerCase();
    return client.client_name.toLowerCase().includes(searchTerm) ||
           (client.company_name && client.company_name.toLowerCase().includes(searchTerm));
  });

  // Helper functions for display mapping
  const getTaxInvoiceDisplay = (value) => {
    // Handle both old and new values for backward compatibility
    if (value === '발급 필요' || value === '필요') return '필요';
    if (value === '숨고 발급' || value === '숨고발급' || value === '숨고') return '숨고';
    return value;
  };
  const getVatDisplay = (value) => {
    if (value === '신고 필요') return '필요';
    return value;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="page-container project-management-table">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>프로젝트 관리</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span className="medium-badge 카톡">카톡</span>
          <span className="medium-badge 오픈챗">오픈챗</span>
          <span className="medium-badge 숨고">숨고</span>
          <span className="medium-badge 채널">채널</span>
          <span className="medium-badge 왓츠앱">왓츠앱</span>
        </div>
      </div>

      <div className="controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'nowrap' }}>
        <input
          type="text"
          className="form-input"
          style={{ minWidth: 180, marginRight: 0 }}
          placeholder="프로젝트명 또는 클라이언트명 검색..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input">
          <option value="">전체 진행</option>
          <option value="진행중">진행중</option>
          <option value="납품">납품</option>
          <option value="완료">완료</option>
          <option value="취소">취소</option>
        </select>
        <select value={filterTaxInvoice} onChange={e => setFilterTaxInvoice(e.target.value)} className="form-input">
          <option value="">전체 세금계산서</option>
          <option value="필요">필요</option>
          <option value="발급">발급</option>
          <option value="불필요">불필요</option>
          <option value="종이">종이</option>
          <option value="숨고">숨고</option>
        </select>
        <select value={filterVat} onChange={e => setFilterVat(e.target.value)} className="form-input">
          <option value="">전체 부가세</option>
          <option value="필요">필요</option>
          <option value="신고">신고</option>
          <option value="불필요">불필요</option>
        </select>
        <select value={filterComprehensive} onChange={e => setFilterComprehensive(e.target.value)} className="form-input">
          <option value="">전체 종합세</option>
          <option value="필요">필요</option>
          <option value="신고">신고</option>
          <option value="불필요">불필요</option>
        </select>
        <select value={filterContractType} onChange={e => setFilterContractType(e.target.value)} className="form-input">
          <option value="">전체 계약 종류</option>
          <option value="계좌이체">계좌이체</option>
          <option value="프리랜서">프리랜서</option>
          <option value="사업자">사업자</option>
        </select>
        <button className="btn btn-primary" onClick={openCreateModal} style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}>
          <Plus size={16} />
          프로젝트 생성
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}>프로젝트명</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em' }}>클라이언트</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>진행</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>입금</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em' }}>금액</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em' }}>부가세</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>계약 기간</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>세금계산서</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>부가세</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>종합세</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', width: '4.5em', padding: '0.5em' }}>계약 종류</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>작업</th>
            </tr>
          </thead>
          <tbody>
            {sortedProjects.map((project) => (
              <tr key={project.id}>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => openDetailModal(project)}>
                  <strong>{project.project_name}</strong>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', width: '9em', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={e => { e.stopPropagation(); openClientDetailModal(project); }}
                    onMouseDown={e => e.stopPropagation()}>
                  <span className={`medium-badge ${project.companies?.medium || project.medium || 'default'}`} style={{ fontSize: '0.8rem' }}>
                    {project.companies?.client_name}
                  </span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`status-badge ${project.status}`}>{project.status}</span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`payment-badge payment-${project.payment_status}`}>{project.payment_status}</span>
                </td>
                <td className="amount" style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.9em' }}>
                  {project.amount ? formatNumberForDisplay(project.amount) : '-'}
                </td>
                <td className="amount" style={{ textAlign: 'center', fontWeight: 'normal', fontSize: '0.9em' }}>
                  {project.vat_amount ? formatNumberForDisplay(project.vat_amount) : '-'}
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', whiteSpace: 'pre-line' }}>
                  {project.contract_date || '-'}
                  {'\n'}
                  {project.deadline || '-'}
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`tax-badge tax-invoice-${project.tax_invoice_status?.replace(/\s+/g, '-')}`}>{getTaxInvoiceDisplay(project.tax_invoice_status)}</span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`vat-badge vat-${project.vat_reporting_status?.replace(/\s+/g, '-')}`}>{getVatDisplay(project.vat_reporting_status)}</span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`comprehensive-badge comprehensive-${project.comprehensive_tax_status?.replace(/\s+/g, '-')}`}>{getVatDisplay(project.comprehensive_tax_status)}</span>
                </td>
                <td style={{ textAlign: 'center', width: '4.5em', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`contract-badge contract-${project.contract_type}`}>{project.contract_type}</span>
                </td>
                <td style={{ padding: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3em' }}>
                    <Edit
                      size={20}
                      style={{ cursor: 'pointer', color: '#6c757d', margin: 0, padding: 0 }}
                      onClick={() => openEditModal(project)}
                    />
                    <Trash2
                      size={20}
                      style={{ cursor: 'pointer', color: '#e74c3c', margin: 0, padding: 0 }}
                      onClick={() => handleDelete(project.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p>총 {sortedProjects.length}개 프로젝트</p>
      </div>

      {/* Create/Edit Project Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="form-overlay">
          <div className="form-modal">
            <h2>{showEditModal ? '프로젝트 수정' : '새 프로젝트 추가'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">프로젝트명 *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.project_name}
                  onChange={(e) => handleInputChange('project_name', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">클라이언트 선택 *</label>
                <div className="client-selector">
                  <div className="search-box" onClick={openClientModal} style={{ cursor: 'pointer' }}>
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="클라이언트 검색..."
                      value={selectedClient ? `${selectedClient.client_name}${selectedClient.company_name ? ` - ${selectedClient.company_name}` : ''}` : ''}
                      className="form-input"
                      readOnly
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={openClientModal}>
                    선택
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">진행</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="form-input"
                  >
                    <option value="진행중">진행중</option>
                    <option value="납품">납품</option>
                    <option value="완료">완료</option>
                    <option value="취소">취소</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">입금</label>
                  <select
                    value={formData.payment_status}
                    onChange={(e) => handleInputChange('payment_status', e.target.value)}
                    className="form-input"
                  >
                    <option value="대기">대기</option>
                    <option value="선금">선금</option>
                    <option value="중도금">중도금</option>
                    <option value="잔금">잔금</option>
                    <option value="숨고">숨고</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">금액</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="1,900,000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">부가세</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.vat_amount}
                    onChange={(e) => handleInputChange('vat_amount', e.target.value)}
                    placeholder="190,000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_business}
                    onChange={(e) => handleInputChange('is_business', e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  사업자
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">세금계산서</label>
                  <select
                    value={formData.tax_invoice_status}
                    onChange={(e) => handleInputChange('tax_invoice_status', e.target.value)}
                    className="form-input"
                  >
                    <option value="필요">필요</option>
                    <option value="발급">발급</option>
                    <option value="불필요">불필요</option>
                    <option value="종이">종이</option>
                    <option value="숨고">숨고</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">부가세 신고</label>
                  <select
                    value={formData.vat_reporting_status}
                    onChange={(e) => handleInputChange('vat_reporting_status', e.target.value)}
                    className="form-input"
                  >
                    <option value="필요">필요</option>
                    <option value="신고">신고</option>
                    <option value="불필요">불필요</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">종합세 신고</label>
                  <select
                    value={formData.comprehensive_tax_status}
                    onChange={(e) => handleInputChange('comprehensive_tax_status', e.target.value)}
                    className="form-input"
                  >
                    <option value="필요">필요</option>
                    <option value="신고">신고</option>
                    <option value="불필요">불필요</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">계약 종류</label>
                  <select
                    value={formData.contract_type}
                    onChange={(e) => handleInputChange('contract_type', e.target.value)}
                    className="form-input"
                  >
                    <option value="계좌이체">계좌이체</option>
                    <option value="프리랜서">프리랜서</option>
                    <option value="사업자">사업자</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">계약일</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.contract_date}
                    onChange={(e) => handleInputChange('contract_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">마감일</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.deadline}
                    onChange={(e) => handleInputChange('deadline', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">계약금 지급일</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.advance_payment_date}
                    onChange={(e) => handleInputChange('advance_payment_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">중도금 지급일</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.interim_payment_date}
                    onChange={(e) => handleInputChange('interim_payment_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">잔금 지급일</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.final_payment_date}
                  onChange={(e) => handleInputChange('final_payment_date', e.target.value)}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {showEditModal ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showClientModal && (
        <div className="form-overlay client-selection-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h2>클라이언트 선택</h2>
              <button onClick={closeClientModal} className="btn-icon">×</button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="클라이언트명 또는 상호명으로 검색..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="form-input"
                  autoFocus
                />
              </div>
              <div className="client-list">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="client-item"
                    onClick={() => selectClient(client)}
                  >
                    <div className="client-info">
                      <div className="client-details">
                        <span className="client-name">{client.client_name}</span>
                        {client.company_name && (
                          <span className="company-name"> - {client.company_name}</span>
                        )}
                      </div>
                      <div className="client-meta">
                        <span className={`medium-badge ${client.medium || 'default'}`}>
                          {client.medium || '기타'}
                        </span>
                        <span className={`business-badge ${client.is_business ? '사업자' : '개인'}`}>
                          {client.is_business ? '사업자' : '개인'}
                        </span>
                        {client.business_number && (
                          <span className="business-number">
                            {client.is_business ? '사업자번호' : '주민번호'}: {client.business_number}
                          </span>
                        )}
                        {client.phone && (
                          <span className="phone">📞 {client.phone}</span>
                        )}
                        {client.email && (
                          <span className="email">✉️ {client.email}</span>
                        )}
                        <span className="address">📍 {client.address || '-'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && detailProject && (
        <div className="form-overlay" style={{ zIndex: 2000, background: 'rgba(0,0,0,0.25)' }}>
          <div className="form-modal" style={{ maxWidth: 500, width: '95vw', borderRadius: 18, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', background: '#fff', padding: 0 }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', padding: '1.2em 1.5em 0.7em 1.5em' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: '1.25em', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {detailProject.project_name}
                  {detailProject.status && (
                    <span className={`status-badge ${detailProject.status}`}>{detailProject.status}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <span className={`medium-badge ${detailProject.companies?.medium || detailProject.medium || 'default'}`}>{detailProject.companies?.medium || detailProject.medium || '기타'}</span>
                  <span style={{ fontWeight: 500, fontSize: '1.05em' }}>{detailProject.companies?.client_name}</span>
                  {detailProject.companies?.company_name && <span style={{ color: '#6b7280', fontSize: '0.95em' }}>({detailProject.companies.company_name})</span>}
                </div>
              </div>
              <button onClick={closeDetailModal} className="btn-icon" style={{ fontSize: 24, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5em 1.5em 1em 1.5em' }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ minWidth: 180 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>계약 기간</div>
                  <div style={{ fontWeight: 500 }}>{detailProject.contract_date || '-'} ~ {detailProject.deadline || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>입금</div>
                  <span className={`payment-badge payment-${detailProject.payment_status}`}>{detailProject.payment_status}</span>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>계약 종류</div>
                  <span className={`contract-badge contract-${detailProject.contract_type}`}>{detailProject.contract_type}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>금액</div>
                  <div style={{ fontWeight: 700 }}>{detailProject.amount ? formatNumberForDisplay(detailProject.amount) : '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>부가세</div>
                  <div>{detailProject.vat_amount ? formatNumberForDisplay(detailProject.vat_amount) : '-'}</div>
                </div>
              </div>
              <div style={{ margin: '18px 0 8px 0', fontWeight: 600, fontSize: '1.05em', color: '#374151' }}>세금/신고 정보</div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>세금계산서</div>
                  <span className={`tax-badge tax-invoice-${detailProject.tax_invoice_status?.replace(/\s+/g, '-')}`}>{detailProject.tax_invoice_status}</span>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>부가세</div>
                  <span className={`vat-badge vat-${detailProject.vat_reporting_status?.replace(/\s+/g, '-')}`}>{detailProject.vat_reporting_status}</span>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>종합세</div>
                  <span className={`comprehensive-badge comprehensive-${detailProject.comprehensive_tax_status?.replace(/\s+/g, '-')}`}>{detailProject.comprehensive_tax_status}</span>
                </div>
              </div>
              <div style={{ margin: '18px 0 8px 0', fontWeight: 600, fontSize: '1.05em', color: '#374151' }}>지급일 정보</div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>계약금 지급일</div>
                  <div>{detailProject.advance_payment_date || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>중도금 지급일</div>
                  <div>{detailProject.interim_payment_date || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>잔금 지급일</div>
                  <div>{detailProject.final_payment_date || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showClientInfoModal && clientDetail && (
        <div className="form-overlay" style={{ zIndex: 2000, background: 'rgba(0,0,0,0.25)' }}>
          <div className="form-modal" style={{ maxWidth: 480, width: '95vw', borderRadius: 18, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', background: '#fff', padding: 0 }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', padding: '1.2em 1.5em 0.7em 1.5em' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`medium-badge ${clientDetail.medium || 'default'}`}>{clientDetail.medium || '기타'}</span>
                <span className={`business-badge ${clientDetail.is_business ? '사업자' : '개인'}`}>{clientDetail.is_business ? '사업자' : '개인'}</span>
                <span style={{ fontWeight: 700, fontSize: '1.1em' }}>{clientDetail.client_name}</span>
                {clientDetail.company_name && <span style={{ color: '#6b7280', fontSize: '0.95em' }}>({clientDetail.company_name})</span>}
              </div>
              <button onClick={closeClientDetailModal} className="btn-icon" style={{ fontSize: 24, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '1.5em 1.5em 1em 1.5em' }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>상호명</div>
                  <div>{clientDetail.company_name || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>사업자번호</div>
                  <div>{clientDetail.business_number || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>사업자 유형</div>
                  <div>{clientDetail.is_business ? '사업자' : '개인'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>이메일</div>
                  <div>{clientDetail.email || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>전화번호</div>
                  <div>{clientDetail.phone || '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>주소</div>
                  <div>{clientDetail.address || '-'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>생성일</div>
                  <div>{clientDetail.created_at ? new Date(clientDetail.created_at).toLocaleString() : '-'}</div>
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ color: '#6b7280', fontSize: '0.95em', marginBottom: 2 }}>수정일</div>
                  <div>{clientDetail.updated_at ? new Date(clientDetail.updated_at).toLocaleString() : '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectManagement;